import { usePostHog } from "posthog-js/react";
import { useEffect, useState } from "react";
import { rampsActions } from "../stores/use-ramps-store";

/**
 * Feature Flag Configuration
 * Add new flags here as they're created in PostHog
 */
export const FEATURE_FLAGS = {
	RAMPS_MIN_AMOUNT_CALCULATOR: "ramps-min-amount-calculator-page",
	RAMPS_MAX_AMOUNT_CALCULATOR: "ramps-max-amount-calculator-page",
	RAMPS_ADDRESS_LOOKUP_ENABLED: "ramps-address-lookup-enabled",
	RAMPS_KYC_POLLING_TIME: "ramps-kyc-polling-time",
	RAMPS_KYC_FLOW_ENABLED: "ramps-kyc-flow-enabled",
	ENABLE_DIRECT_TO_ACCT: "ramps-enable-direct-to-account-feature",
	// A/B Experiment: Controls which calculator UI variant is shown
	RAMPS_NEW_CALCULATOR_PAGE_ENABLED: "ramps-new-calculator-page-enabled",
} as const;

/**
 * Type-safe feature flag payloads
 * Define the expected payload shape for each flag
 */
export interface FeatureFlagPayloads {
	[FEATURE_FLAGS.RAMPS_MIN_AMOUNT_CALCULATOR]: {
		MIN_AMOUNT?: number | string; // PostHog might send uppercase
		enabled?: boolean;
	};
	[FEATURE_FLAGS.RAMPS_MAX_AMOUNT_CALCULATOR]: {
		MAX_AMOUNT?: number | string; // PostHog might send uppercase
		enabled?: boolean;
	};
	[FEATURE_FLAGS.RAMPS_KYC_POLLING_TIME]: {
		POLLING_TIME?: number | string; // PostHog might send uppercase
		enabled?: boolean;
	};
	[FEATURE_FLAGS.ENABLE_DIRECT_TO_ACCT]: {
		enabled?: boolean;
		supportedCountries?: string[]; // Array of ISO3 country codes (e.g., ["COL", "MEX"])
	};
	// Add more payload types here
	// [FEATURE_FLAGS.EXAMPLE_FLAG]: {
	//   someValue?: string
	// }
}

/**
 * Custom hook to manage PostHog feature flags
 * Handles loading, checking, and accessing feature flag payloads
 */
export function useFeatureFlags() {
	const posthog = usePostHog();
	const [flagsLoaded, setFlagsLoaded] = useState(false);
	const [flags, setFlags] = useState<Record<string, boolean>>({});

	useEffect(() => {
		if (!posthog) {
			return;
		}

		// Wait for feature flags to load
		posthog.onFeatureFlags(() => {
			setFlagsLoaded(true);

			// Check all flags and store their enabled state
			const flagStates: Record<string, boolean> = {};

			Object.values(FEATURE_FLAGS).forEach((flagKey) => {
				const isEnabled = posthog.isFeatureEnabled(flagKey) ?? false;
				flagStates[flagKey] = isEnabled;
			});
			setFlags(flagStates);

			// Handle min amount calculator flag
			const isMinAmountEnabled = posthog.isFeatureEnabled(
				FEATURE_FLAGS.RAMPS_MIN_AMOUNT_CALCULATOR,
			);

			if (isMinAmountEnabled) {
				const payload = posthog.getFeatureFlagPayload(
					FEATURE_FLAGS.RAMPS_MIN_AMOUNT_CALCULATOR,
				) as FeatureFlagPayloads[typeof FEATURE_FLAGS.RAMPS_MIN_AMOUNT_CALCULATOR];

				const minAmountValue = payload?.MIN_AMOUNT;

				if (minAmountValue) {
					const minAmount =
						typeof minAmountValue === "string"
							? parseFloat(minAmountValue)
							: minAmountValue;

					if (!Number.isNaN(minAmount) && minAmount > 0) {
						rampsActions.setMinAmount(minAmount);
					}
				}
			}

			// Handle max amount calculator flag
			const isMaxAmountEnabled = posthog.isFeatureEnabled(
				FEATURE_FLAGS.RAMPS_MAX_AMOUNT_CALCULATOR,
			);

			if (isMaxAmountEnabled) {
				const payload = posthog.getFeatureFlagPayload(
					FEATURE_FLAGS.RAMPS_MAX_AMOUNT_CALCULATOR,
				) as FeatureFlagPayloads[typeof FEATURE_FLAGS.RAMPS_MAX_AMOUNT_CALCULATOR];

				const maxAmountValue = payload?.MAX_AMOUNT;

				if (maxAmountValue) {
					const maxAmount =
						typeof maxAmountValue === "string"
							? parseFloat(maxAmountValue)
							: maxAmountValue;

					if (!Number.isNaN(maxAmount) && maxAmount > 0) {
						rampsActions.setMaxAmount(maxAmount);
					}
				}
			}

			// Handle KYC polling time flag
			const isKycPollingTimeEnabled = posthog.isFeatureEnabled(
				FEATURE_FLAGS.RAMPS_KYC_POLLING_TIME,
			);

			// Get payload regardless of enabled state to check if it exists
			const payload = posthog.getFeatureFlagPayload(
				FEATURE_FLAGS.RAMPS_KYC_POLLING_TIME,
			) as FeatureFlagPayloads[typeof FEATURE_FLAGS.RAMPS_KYC_POLLING_TIME];

			if (isKycPollingTimeEnabled) {
				const pollingTimeValue = payload?.POLLING_TIME;

				if (pollingTimeValue) {
					const pollingTime =
						typeof pollingTimeValue === "string"
							? parseFloat(pollingTimeValue)
							: pollingTimeValue;

					if (!Number.isNaN(pollingTime) && pollingTime > 0) {
						rampsActions.setKycPollingTime(pollingTime);
					} else {
						console.warn(
							`[FeatureFlags] Invalid polling time value: ${pollingTimeValue}`,
						);
					}
				} else {
					console.warn(
						"[FeatureFlags] KYC polling time flag enabled but no POLLING_TIME in payload",
					);
				}
			}

			// Handle KYC flow enabled flag
			const isKycFlowEnabled = posthog.isFeatureEnabled(
				FEATURE_FLAGS.RAMPS_KYC_FLOW_ENABLED,
			);
			rampsActions.setKycFlowEnabled(isKycFlowEnabled ?? false);

			// Add handlers for other flags here in the future
		});
	}, [posthog]);

	/**
	 * Check if a specific feature flag is enabled
	 */
	const isFeatureEnabled = (flagKey: string): boolean => {
		if (!posthog || !flagsLoaded) return false;
		return posthog.isFeatureEnabled(flagKey) ?? false;
	};

	/**
	 * Get the payload for a specific feature flag with type safety
	 */
	const getFeatureFlagPayload = <K extends keyof FeatureFlagPayloads>(
		flagKey: K,
	): FeatureFlagPayloads[K] | undefined => {
		if (!posthog || !flagsLoaded) return undefined;
		return posthog.getFeatureFlagPayload(flagKey) as FeatureFlagPayloads[K];
	};

	return {
		flagsLoaded,
		flags,
		isFeatureEnabled,
		getFeatureFlagPayload,
		posthog, // Direct access if needed
	};
}
